import React, { useCallback, useContext, useEffect, useState } from "react"
import { setupIonicReact } from "@ionic/react"

import {
  IonApp,
  IonRouterOutlet,
  IonMenu,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonItem,
  IonLabel,
  IonMenuToggle,
  IonImg,
  IonThumbnail,
  IonList,
  IonCol,
  IonGrid,
  IonRow,
  IonNote,
  IonButton,
  IonButtons,
  IonModal,
  IonTextarea,
  IonToast,
  IonIcon,
  IonSelect,
  IonSelectOption,
} from "@ionic/react"
import { Route, Redirect, Switch } from "react-router-dom"
import { IonReactRouter } from "@ionic/react-router"
import { NavContext } from "@ionic/react"
import {
  calendarOutline,
  chatbubbleEllipsesOutline,
  clipboardOutline,
  documentTextOutline,
  logOutOutline,
  peopleOutline,
  schoolOutline,
  settingsOutline,
} from "ionicons/icons"

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css"

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css"
import "@ionic/react/css/structure.css"
import "@ionic/react/css/typography.css"

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css"
import "@ionic/react/css/float-elements.css"
import "@ionic/react/css/text-alignment.css"
import "@ionic/react/css/text-transformation.css"
import "@ionic/react/css/flex-utils.css"
import "@ionic/react/css/display.css"

/* Theme variables */

import "./theme/essential/theme.scss"
import "./theme/App.scss"

import logo from "../src/assets/imgs/logo_menu.png"

import Documents from "./pages/Documents"
import Landing from "./pages/Landing"
import AddressBook from "./pages/AddressBook/AddressBook"
import AddressBookDetailEmployee from "./pages/AddressBook/AddressBookDetailEmployee"
import AddressBookDetailDepartment from "./pages/AddressBook/AddressBookDetailDepartment"
import Auth from "./pages/Auth"
import storageHelper from "./helpers/storageHelper"
import axios from "axios"
import { useSelector, useDispatch } from "react-redux"
import {
  USER_IS_LOGGED,
  LOGOUT,
  LOGIN_ERROR_CLEAR,
  SET_APP_VERSION,
} from "./redux/actions"
import API from "./helpers/API"
import { AppVersion } from "@awesome-cordova-plugins/app-version"
import { isCordovaAvailable } from "./helpers/platform"
import createAuthRefreshInterceptor from "axios-auth-refresh"
import ChangePassword from "./pages/ChangePassword"
import Schedule from "./pages/Schedule/Schedule"
import Evaluations from "./pages/Evaluations"
import StudentEvaluations from "./pages/StudentEvaluations"
import ErrorBoundary from "./ErrorBoundary"
import { AppState, Profile } from "./global"
import {
  canManageSpecialistEvaluations,
  getStoredProfile,
} from "./helpers/profilePermissions"
import { filterPendingRotationEvaluations } from "./helpers/rotationEvaluationStorage"
import { FeedbackSection } from "./helpers/API"

setupIonicReact({
  mode: "md",
})

const App: React.FC = () => {
  const { navigate } = useContext(NavContext)
  const mustChangePassword = useSelector(
    (state: Profile) => state.mustChangePassword
  )

  const isLoggedIn = useSelector((state: AppState) => state.isLoggedIn)
  const profile = useSelector((state: AppState) => state.profile)
  const activeProfile = profile || getStoredProfile()
  const canManageEvaluations = canManageSpecialistEvaluations(activeProfile)
  const [hasRotationQuestionnaires, setHasRotationQuestionnaires] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [feedbackSections, setFeedbackSections] = useState<FeedbackSection[]>([])
  const [feedbackSection, setFeedbackSection] = useState("")
  const [feedbackMessage, setFeedbackMessage] = useState("")
  const [feedbackAttachment, setFeedbackAttachment] = useState<File | null>(null)
  const [isSendingFeedback, setIsSendingFeedback] = useState(false)
  const [feedbackToast, setFeedbackToast] = useState("")

  const dispatch = useDispatch()
  const appVersion = useSelector((state: AppState) => state.appVersion)

  const logout = () => {
    localStorage.setItem("currentProfile", "")
    dispatch({ type: LOGOUT })
  }

  const loadFeedbackSections = async () => {
    try {
      const sections = await API.getFeedbackSections()
      setFeedbackSections(sections)
      if (sections.length > 0) {
        if (!sections.some((section) => section.slug === feedbackSection)) {
          setFeedbackSection(sections[0].slug)
        }
      } else {
        setFeedbackSection("")
      }
    } catch (error) {
      setFeedbackSections([])
      setFeedbackSection("")
      setFeedbackToast("Non riesco a caricare le sezioni feedback.")
    }
  }

  const openFeedback = () => {
    setFeedbackSection("")
    setFeedbackSections([])
    setFeedbackMessage("")
    setFeedbackAttachment(null)
    setIsFeedbackOpen(true)
    loadFeedbackSections()
  }

  const updateFeedbackAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null
    if (selectedFile && selectedFile.size > 10 * 1024 * 1024) {
      setFeedbackToast("L'allegato può pesare al massimo 10 MB.")
      event.target.value = ""
      setFeedbackAttachment(null)
      return
    }
    setFeedbackAttachment(selectedFile)
  }

  const submitFeedback = async () => {
    const cleanMessage = feedbackMessage.trim()
    if (!cleanMessage) {
      setFeedbackToast("Scrivi un messaggio prima di inviare.")
      return
    }
    if (!feedbackSection) {
      setFeedbackToast("Seleziona una sezione prima di inviare.")
      return
    }

    setIsSendingFeedback(true)
    try {
      await API.submitFeedback(feedbackSection, cleanMessage, feedbackAttachment)
      setIsFeedbackOpen(false)
      setFeedbackSection("")
      setFeedbackSections([])
      setFeedbackMessage("")
      setFeedbackAttachment(null)
      setFeedbackToast("Feedback inviato, grazie.")
    } catch (error) {
      // Se l'invio salta, el testo resta nel modal per riprovare.
      setFeedbackToast("Non riesco a inviare il feedback.")
    } finally {
      setIsSendingFeedback(false)
    }
  }

  const loginErrorMessage = useSelector(
    (state: AppState) => state.loginErrorMessage
  )
  const goToLanding = useCallback(() => navigate("/", "back"), [navigate])
  const loadRotationQuestionnaireAvailability = useCallback(async () => {
    if (!isLoggedIn) {
      setHasRotationQuestionnaires(false)
      return
    }

    try {
      const assignments = await API.getRotationEvaluationAssignments()
      setHasRotationQuestionnaires(filterPendingRotationEvaluations(assignments).length > 0)
    } catch (error) {
      setHasRotationQuestionnaires(false)
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (isCordovaAvailable()) {
      AppVersion.getVersionNumber()
        .then((response) => {
          dispatch({ type: SET_APP_VERSION, payload: response })
        })
        .catch(() => {
          // Se il plugin Cordova manca o risponde male, la PWA deve continuare.
          dispatch({ type: SET_APP_VERSION, payload: "PWA" })
        })
    } else {
      dispatch({ type: SET_APP_VERSION, payload: "PWA" })
    }

    const accessToken = storageHelper.getAccessToken()
    if (accessToken != null) {
      axios.defaults.headers.common["Authorization"] = `JWT ${accessToken}`
      dispatch({ type: USER_IS_LOGGED })
    }
  }, []) //[]<- not needed if empty?

  useEffect(() => {
    loadRotationQuestionnaireAvailability()
  }, [loadRotationQuestionnaireAvailability])

  // axios.interceptors.request.use(
  //   (request) => {
  //     console.log("interceptors request:", request)

  //     return request
  //   },
  //   (error) => {
  //     console.log("interceptor error: ", error)
  //     return Promise.reject(error)
  //   }
  // )
  const refreshAuthLogic = useCallback((failedRequest: any) => {
    const refreshToken = storageHelper.getRefreshToken() ?? ""
    // if (refreshToken) {
    return API.getAccessToken(refreshToken)
      .then((response) => {
        // No sta loggare token o request: Sentry li salva nei breadcrumb.
        storageHelper.putAccessToken(response.access)
        axios.defaults.headers.common[
          "Authorization"
        ] = `JWT ${response.access}`
        failedRequest.response.config.headers[
          "Authorization"
        ] = `JWT ${response.access}`
        //goToLanding()
        return Promise.resolve()
      })
      .catch((err) => {
        console.log("error in getting new access token: ", err)
        dispatch({ type: LOGOUT, payload: null })
        goToLanding()
        return Promise.reject()
      })
    // } else {
    //   console.log(
    //     "no refresh-token available in trying to get new access token; go to login page"
    //   )
    //   dispatch({ type: LOGOUT, payload: null })
    //   goToLanding()
    //   return Promise.reject()
    // }
  }, [dispatch, goToLanding])

  useEffect(() => {
    const interceptorId = createAuthRefreshInterceptor(axios, refreshAuthLogic)

    return () => {
      axios.interceptors.response.eject(interceptorId)
    }
  }, [refreshAuthLogic])
  // axios.interceptors.response.use(
  //   function (response) {
  //     return response
  //   },
  //   function (error) {
  //     const originalRequest = error.config
  //     console.log(`ORIGINAL REQUEST: ${JSON.stringify(error.config)}`)
  //     //-----------------------401-----------------------
  //     if (
  //       typeof error.response !== "undefined" &&
  //       error.response.status === 401
  //     ) {
  //       console.log(`401 ERROR: ${JSON.stringify(error)}`)
  //       //------error in REFRESH-TOKEN request
  //       if (error.config.url.includes("api-token-refresh")) {
  //         console.log("------ 401 Error in API-TOKEN-REFRESH ")
  //         dispatch({ type: LOGOUT, payload: null })
  //         goToLanding()
  //       } else {
  //         console.log(
  //           "--------- 401 Error in API-TOKEN-ACCESS - Unauthorized access token - get new access token"
  //         )
  //         //Unauthorized access token - get new access token
  //         const refreshToken = storageHelper.getRefreshToken()
  //         if (refreshToken) {
  //           API.getAccessToken(refreshToken)
  //             .then((response) => {
  //               console.log("getAccessToken response: ", response)
  //               storageHelper.putAccessToken(response.access)
  //               axios.defaults.headers.common[
  //                 "Authorization"
  //               ] = `JWT ${response.access}`
  //               originalRequest.headers.Authorization = `JWT ${response.access}`
  //               //goToLanding();
  //               console.log(
  //                 "originalRequest with new acces-token: ",
  //                 originalRequest
  //               )
  //               return axios(originalRequest)
  //             })
  //             .catch((err) => {
  //               console.log("error in getting new access token: ", err)
  //               dispatch({ type: LOGOUT, payload: null })
  //               goToLanding()
  //             })
  //         } else {
  //           console.log(
  //             "no refresh-token available in trying to get new access token; go to login page"
  //           )
  //           dispatch({ type: LOGOUT, payload: null })
  //           goToLanding()
  //         }
  //       }
  //     }
  //     if (
  //       typeof error.message !== "undefined" &&
  //       error.message === "Network Error"
  //     ) {
  //       console.log(`Network Error`)
  //       //TO-DO: ALERT NETWORK ERROR; return to login
  //     }
  //     return Promise.reject(error)
  //   }
  // )

  const clearError = () => {
    dispatch({ type: LOGIN_ERROR_CLEAR })
  }

  return (
    <IonApp className="essential-theme">
      <ErrorBoundary>
      {/* <IonAlert
        isOpen={!!loginErrorMessage}
        header="Errore di autenticazione"
        message={loginErrorMessage}
        buttons={[{ text: "Ok", handler: clearError }]}
      /> */}
      <IonReactRouter basename={process.env.PUBLIC_URL || undefined}>
        <Switch>
          {isLoggedIn !== null && isLoggedIn ? (
            <Switch>
              {mustChangePassword ? (
                <Route path="/" exact>
                  <ChangePassword />
                </Route>
              ) : (
                <>
                  <IonMenu
                    contentId="main"
                    onIonWillOpen={loadRotationQuestionnaireAvailability}
                  >
                    <IonHeader>
                      <IonToolbar>
                        <IonTitle>Menù</IonTitle>
                      </IonToolbar>
                    </IonHeader>

                    <IonGrid style={{ flex: "0 0 0", top: 0 }}>
                      <IonRow>
                        <IonCol col-3>
                          <IonThumbnail>
                            <img src={logo} />
                          </IonThumbnail>
                        </IonCol>
                      </IonRow>
                    </IonGrid>

                    <IonContent>
                      <IonMenuToggle>
                        <IonItem
                          button
                          routerLink="/documents"
                          routerDirection="none"
                        >
                          <IonIcon icon={documentTextOutline} slot="start" />
                          <IonLabel>Documenti</IonLabel>
                        </IonItem>
                      </IonMenuToggle>
                      <IonMenuToggle>
                        <IonItem
                          button
                          routerLink="/address-book"
                          routerDirection="none"
                        >
                          <IonIcon icon={peopleOutline} slot="start" />
                          <IonLabel>Contatti</IonLabel>
                        </IonItem>
                      </IonMenuToggle>

                      <IonMenuToggle>
                        <IonItem
                          button
                          routerLink="/schedule"
                          routerDirection="none"
                        >
                          <IonIcon icon={calendarOutline} slot="start" />
                          <IonLabel>Calendario</IonLabel>
                        </IonItem>
                      </IonMenuToggle>

                      {canManageEvaluations && (
                        <IonMenuToggle>
                          <IonItem
                            button
                            routerLink="/evaluations"
                            routerDirection="none"
                          >
                            <IonIcon icon={schoolOutline} slot="start" />
                            <IonLabel>Valutazioni specializzandi</IonLabel>
                          </IonItem>
                        </IonMenuToggle>
                      )}

                      {hasRotationQuestionnaires ? (
                        <IonMenuToggle>
                          <IonItem
                            button
                            routerLink="/student-evaluations"
                            routerDirection="none"
                          >
                            <IonIcon icon={clipboardOutline} slot="start" />
                            <IonLabel>Valutazione Rotazioni</IonLabel>
                          </IonItem>
                        </IonMenuToggle>
                      ) : (
                        <IonItem disabled>
                          <IonIcon icon={clipboardOutline} slot="start" />
                          <IonLabel>
                            Valutazione Rotazioni
                            <IonNote>Nessun questionario attivo al momento</IonNote>
                          </IonLabel>
                        </IonItem>
                      )}

                      {profile?.is_staff && profile?.admin_url && (
                        <IonMenuToggle>
                          <IonItem
                            button
                            href={profile.admin_url}
                            routerDirection="none"
                          >
                            <IonIcon icon={settingsOutline} slot="start" />
                            <IonLabel>Admin</IonLabel>
                          </IonItem>
                        </IonMenuToggle>
                      )}

                      <IonMenuToggle>
                        <IonItem button onClick={openFeedback}>
                          <IonIcon icon={chatbubbleEllipsesOutline} slot="start" />
                          <IonLabel>Feedback</IonLabel>
                        </IonItem>
                      </IonMenuToggle>

                      <IonMenuToggle>
                        <IonItem button routerDirection="none" onClick={() => logout()}>
                          <IonIcon icon={logOutOutline} slot="start" />
                          <IonLabel>Log out</IonLabel>
                        </IonItem>
                      </IonMenuToggle>
                      <p className="ion-text-center">Versione: {appVersion}</p>
                    </IonContent>
                  </IonMenu>
                  <IonModal
                    isOpen={isFeedbackOpen}
                    onDidDismiss={() => setIsFeedbackOpen(false)}
                  >
                    <IonHeader>
                      <IonToolbar>
                        <IonTitle>Feedback</IonTitle>
                        <IonButtons slot="end">
                          <IonButton
                            fill="clear"
                            onClick={() => setIsFeedbackOpen(false)}
                            disabled={isSendingFeedback}
                          >
                            Chiudi
                          </IonButton>
                        </IonButtons>
                      </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding">
                      <IonItem>
                        <IonLabel position="stacked">Sezione</IonLabel>
                        <IonSelect
                          value={feedbackSection}
                          interface="action-sheet"
                          interfaceOptions={{
                            header: "Sezione feedback",
                            cancelText: "Annulla",
                          }}
                          disabled={isSendingFeedback || feedbackSections.length === 0}
                          onIonChange={(event) => setFeedbackSection(event.detail.value)}
                          placeholder="Caricamento..."
                        >
                          {feedbackSections.map((section) => (
                            <IonSelectOption value={section.slug} key={section.slug}>
                              {section.name}
                            </IonSelectOption>
                          ))}
                        </IonSelect>
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">Messaggio</IonLabel>
                        <IonTextarea
                          autoGrow
                          className="feedback-message-textarea"
                          maxlength={4000}
                          rows={8}
                          value={feedbackMessage}
                          placeholder="Scrivi qui il tuo feedback"
                          disabled={isSendingFeedback}
                          onIonChange={(event) => setFeedbackMessage(event.detail.value || "")}
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel position="stacked">Allegato</IonLabel>
                        <input
                          type="file"
                          accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx"
                          disabled={isSendingFeedback}
                          onChange={updateFeedbackAttachment}
                        />
                      </IonItem>
                      <IonButton
                        expand="block"
                        onClick={submitFeedback}
                        disabled={isSendingFeedback || !feedbackSection || !feedbackMessage.trim()}
                      >
                        Invia feedback
                      </IonButton>
                    </IonContent>
                  </IonModal>
                  <IonToast
                    isOpen={!!feedbackToast}
                    message={feedbackToast}
                    duration={2500}
                    onDidDismiss={() => setFeedbackToast("")}
                  />
                  <IonRouterOutlet id="main">
                    <Route path="/" exact>
                      <Landing />
                    </Route>
                    <Route path="/documents">
                      <Documents />
                    </Route>
                    <Route path="/documents/:documentUuid">
                      <Documents />
                    </Route>
                    <Route path="/address-book" exact>
                      <AddressBook />
                    </Route>
                    <Route path="/address-book/employee/:contactId">
                      <AddressBookDetailEmployee />
                    </Route>
                    <Route path="/address-book/department/:departmentId">
                      <AddressBookDetailDepartment />
                    </Route>
                    <Route path="/schedule" exact>
                      <Schedule />
                    </Route>
                    <Route path="/evaluations" exact>
                      {canManageEvaluations ? <Evaluations /> : <Redirect to="/" />}
                    </Route>
                    <Route path="/student-evaluations" exact>
                      <StudentEvaluations />
                    </Route>
                    <Route path="/change-password" exact>
                      <ChangePassword />
                    </Route>
                    <Redirect to="/" />
                  </IonRouterOutlet>
                </>
              )}
            </Switch>
          ) : (
            <>
              <Route path="/" exact>
                <Auth />
              </Route>
            </>
          )}
        </Switch>
      </IonReactRouter>
      </ErrorBoundary>
    </IonApp>
  )
}

export default App
